import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from 'ui';

import type { PrerequisiteStatus } from '../../../types/radar';

import './_prerequisite-progress-bar.scss';

type PrerequisiteProgressBarProps = {
  currentKnowledge: number;
  onClick?: () => void;
  statement: string;
  status: PrerequisiteStatus;
};

function getColorClass(status: PrerequisiteStatus): string {
  if (status === 'MASTERED') return 'mastered';
  return 'reviewing';
}

function getStatusKey(status: PrerequisiteStatus): string {
  if (status === 'MASTERED') return 'radar_detail_prereq_mastered';
  return 'radar_detail_prereq_rollback';
}

function formatPercentage(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/**
 * PrerequisiteProgressBar - Visualizes the current knowledge of a prerequisite KC
 * with a progress bar, a marker, the status pill and the percentage label.
 */
function PrerequisiteProgressBar({ currentKnowledge, onClick, statement, status }: PrerequisiteProgressBarProps): JSX.Element {
  const { t } = useTranslation(['chatbot']);

  const levelClass = getColorClass(status);
  const statusKey = getStatusKey(status);
  const percentageLabel = formatPercentage(currentKnowledge);

  function handleClick(): void {
    if (onClick) onClick();
  }

  const rootClassName = `prerequisite-progress-bar prerequisite-progress-bar--${levelClass}`;
  const nameClassName = `prerequisite-progress-bar__name${onClick ? ' prerequisite-progress-bar__name--clickable' : ''}`;

  return (
    <div className={rootClassName}>
      {onClick ? (
        <button className={nameClassName} onClick={handleClick} type="button">
          {statement}
        </button>
      ) : (
        <span className={nameClassName}>{statement}</span>
      )}
      <div className="prerequisite-progress-bar__bar-wrapper">
        <span className="prerequisite-progress-bar__marker" style={{ left: percentageLabel }}>
          <Icon size="md" name="chevron-down-fill" />
        </span>
        <div className="prerequisite-progress-bar__bar">
          <div className="prerequisite-progress-bar__bar-fill" style={{ width: percentageLabel }} />
        </div>
      </div>
      <div className="prerequisite-progress-bar__footer">
        <span className={`prerequisite-progress-bar__status prerequisite-progress-bar__status--${levelClass}`}>{t(`chatbot:${statusKey}`)}</span>
        <span className="prerequisite-progress-bar__mastery">
          {t('chatbot:radar_detail_content_mastery')} <strong>{percentageLabel}</strong>
        </span>
      </div>
    </div>
  );
}

export default PrerequisiteProgressBar;
